<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import CountryCodeSelect from './CountryCodeSelect.vue';
import DocumentRequestTemplatesEditor from '../../tickets/categories/DocumentRequestTemplatesEditor.vue';

const props = defineProps({
  field: { type: Object, required: true },
  // Accepted (unused here) so PageEditor can pass it uniformly to both FieldCard
  // and GroupFieldCard via <component :is>. Only GroupFieldCard reads it.
  usedMappedAttributes: { type: Array, default: () => [] },
  // Full ordered page list + the index of the page this field lives on, used to
  // offer branch ("Go to…") targets for select/checkbox options.
  pages: { type: Array, default: () => [] },
  pageIndex: { type: Number, default: 0 },
  // Flat field list (value + label) for conditional-rule dependency pickers.
  availableFields: { type: Array, default: () => [] },
});

const emit = defineEmits(['update', 'delete', 'dragstart']);

const { t } = useI18n();

const { isCloudFeatureEnabled } = useAccount();
const isContractsEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.CONTRACTS)
);

const showConfig = ref(false);

const FIELD_TYPE_STYLES = {
  text: { label: 'Text', class: 'text-n-brand bg-n-blue-2' },
  textarea: { label: 'Textarea', class: 'text-amber-400 bg-amber-400/10' },
  email: { label: 'Email', class: 'text-n-brand bg-n-blue-2' },
  phone: { label: 'Phone', class: 'text-n-brand bg-n-blue-2' },
  number: { label: 'Number', class: 'text-n-brand bg-n-blue-2' },
  select: { label: 'Select', class: 'text-emerald-400 bg-emerald-400/10' },
  checkbox: { label: 'Checkbox', class: 'text-emerald-400 bg-emerald-400/10' },
  date: { label: 'Date', class: 'text-violet-400 bg-violet-400/10' },
  file: { label: 'File', class: 'text-purple-400 bg-purple-400/10' },
  documents: { label: 'Documents', class: 'text-purple-400 bg-purple-400/10' },
  address: { label: 'Address', class: 'text-sky-400 bg-sky-400/10' },
  attribute: { label: 'Attribute', class: 'text-teal-400 bg-teal-400/10' },
};

const typeStyle = computed(
  () => FIELD_TYPE_STYLES[props.field.field_type] || FIELD_TYPE_STYLES.text
);

const hasMappedAttribute = computed(() => !!props.field.mapped_attribute);

const mappedAttributeHint = computed(() => {
  const mapped = props.field.mapped_attribute || '';
  if (mapped.startsWith('ticket.'))
    return t('FORM_BUILDER.FIELD.MAPS_TO_TICKET');
  if (mapped.startsWith('contact.'))
    return t('FORM_BUILDER.FIELD.MAPS_TO_CONTACT');
  return t('FORM_BUILDER.FIELD.MAPS_TO_ATTR');
});

function updateLabel(e) {
  emit('update', { ...props.field, label: e.target.value });
}

function updatePlaceholder(e) {
  emit('update', { ...props.field, placeholder: e.target.value });
}

// Help text revealed by a "?" badge next to the field label on the public form.
function updateTooltip(e) {
  emit('update', { ...props.field, tooltip: e.target.value });
}

function updateRequired(val) {
  emit('update', { ...props.field, required: val });
}

// Visible defaults to true; a hidden field is not rendered and is meant to be
// pre-filled from URL query parameters.
const isVisible = computed(() => props.field.visible !== false);

function updateVisible(val) {
  emit('update', { ...props.field, visible: val });
}

function updateValidation(e) {
  emit('update', { ...props.field, validation_regex: e.target.value });
}

// Predefined / default value, pre-fills the field without a URL parameter.
// A URL prefill (applied after init on the public form) overrides this.
function updateDefault(e) {
  emit('update', { ...props.field, default: e.target.value });
}

function updateDefaultBool(val) {
  emit('update', { ...props.field, default: val });
}

function updateOptions(e) {
  const options = e.target.value.split('\n').filter(o => o.trim());
  // Drop any branch routes whose option label no longer exists.
  const existing = props.field.option_routes || {};
  const option_routes = Object.fromEntries(
    Object.entries(existing).filter(([key]) => options.includes(key))
  );
  emit('update', { ...props.field, options, option_routes });
}

// Display variant for select fields: 'dropdown' (default) or 'buttons'.
function updateVariant(e) {
  const next = { ...props.field };
  if (e.target.value === 'buttons') {
    next.variant = 'buttons';
  } else {
    delete next.variant;
    delete next.button_layout;
  }
  emit('update', next);
}

// Button layout: 'horizontal' (default, side by side) or 'vertical' (stacked).
function updateButtonLayout(e) {
  const next = { ...props.field };
  if (e.target.value === 'vertical') {
    next.button_layout = 'vertical';
  } else {
    delete next.button_layout;
  }
  emit('update', next);
}

function updateMappedAttribute(e) {
  emit('update', { ...props.field, mapped_attribute: e.target.value });
}

// Only contact.* fields can overwrite a value on an existing contact.
const isContactMappedField = computed(() =>
  (props.field.mapped_attribute || '').startsWith('contact.')
);

function toggleOverwriteExisting() {
  emit('update', {
    ...props.field,
    overwrite_existing: !props.field.overwrite_existing,
  });
}

function updateMaxFiles(e) {
  emit('update', {
    ...props.field,
    max_files: parseInt(e.target.value, 10) || 5,
  });
}

function updateMaxFileSize(e) {
  emit('update', {
    ...props.field,
    max_file_size: parseInt(e.target.value, 10) || 10,
  });
}

function handleDragStart(e) {
  emit('dragstart', e);
}

function toggleConfig() {
  showConfig.value = !showConfig.value;
}

const isChoiceType = computed(() =>
  ['select', 'checkbox'].includes(props.field.field_type)
);

const isSelectType = computed(() => props.field.field_type === 'select');

const isCheckboxType = computed(() => props.field.field_type === 'checkbox');

const isFileType = computed(() => props.field.field_type === 'file');

// A "documents" field collects DocumentRequest-backed required documents inline on the
// public form (additional docs defined here + optionally the subcategory's templates).
const isDocumentsType = computed(() => props.field.field_type === 'documents');

const includeSubcategoryTemplates = computed(
  () => props.field.include_subcategory_templates !== false
);

function updateDocuments(documents) {
  emit('update', { ...props.field, documents });
}

function toggleIncludeSubcategoryTemplates(on) {
  emit('update', { ...props.field, include_subcategory_templates: on });
}

const isAddressType = computed(() => props.field.field_type === 'address');

// Google place components that an address field can auto-fill into other fields.
const ADDRESS_COMPONENTS = [
  'street',
  'number',
  'colonia',
  'postal_code',
  'city',
  'state',
];

// Address options: autocomplete (default on) and map (default off), independent.
const addressAutocompleteOn = computed(() => props.field.autocomplete !== false);
const addressMapOn = computed(() => props.field.show_map === true);

function toggleAutocomplete(on) {
  const next = { ...props.field };
  if (on) delete next.autocomplete;
  else next.autocomplete = false;
  emit('update', next);
}

function toggleAddressMap(on) {
  const next = { ...props.field };
  if (on) next.show_map = true;
  else delete next.show_map;
  emit('update', next);
}

// Lock auto-filled fields so they're set only via the search/map.
const addressLockOn = computed(() => props.field.lock_fields === true);
function toggleAddressLock(on) {
  const next = { ...props.field };
  if (on) next.lock_fields = true;
  else delete next.lock_fields;
  emit('update', next);
}

function autofillTarget(component) {
  return props.field.autofill?.[component] || '';
}

function setAutofill(component, targetId) {
  const next = { ...(props.field.autofill || {}) };
  if (targetId) {
    next[component] = targetId;
  } else {
    delete next[component];
  }
  emit('update', { ...props.field, autofill: next });
}

// When the field is locked, individual components can be exempted so they stay
// editable (e.g. número exterior, which the pin often misses). The per-row toggle
// shows the "blocked" state: on = locked, off = editable.
function isComponentEditable(component) {
  return (props.field.unlocked_components || []).includes(component);
}

function isComponentBlocked(component) {
  return addressLockOn.value && !isComponentEditable(component);
}

function toggleComponentEditable(component, editable) {
  const current = props.field.unlocked_components || [];
  const next = editable
    ? [...new Set([...current, component])]
    : current.filter(item => item !== component);
  const updated = { ...props.field };
  if (next.length) updated.unlocked_components = next;
  else delete updated.unlocked_components;
  emit('update', updated);
}

// HTML input type for the predefined value editor (text/number/date fields).
const defaultInputType = computed(() => {
  if (props.field.field_type === 'number') return 'number';
  if (props.field.field_type === 'date') return 'date';
  return 'text';
});

const isPhoneType = computed(() => props.field.field_type === 'phone');

function updateDefaultCountry(countryId) {
  emit('update', { ...props.field, default_country: countryId });
}

// Biases the address autocomplete (Google Places region) + map to a country.
// Stored uppercase ISO (e.g. 'VE'); the public renderer lowercases it for Places.
function updateAddressCountry(countryId) {
  emit('update', { ...props.field, country: countryId });
}

const optionsText = computed(() => (props.field.options || []).join('\n'));

// "Go to…" targets for per-option branching: next page, any other page, or submit.
// The current page is excluded so an option cannot loop back onto itself.
const targetOptions = computed(() => {
  const pageTargets = (props.pages || [])
    .map((p, idx) => ({ id: p.id, idx, title: p.title }))
    .filter(p => p.idx !== props.pageIndex && p.id)
    .map(p => ({
      value: p.id,
      label: p.title || t('FORM_BUILDER.FIELD.LOGIC.PAGE_N', { n: p.idx + 1 }),
    }));
  return [
    { value: '__next__', label: t('FORM_BUILDER.FIELD.LOGIC.NEXT') },
    ...pageTargets,
    { value: '__submit__', label: t('FORM_BUILDER.FIELD.LOGIC.SUBMIT') },
  ];
});

function routeFor(optionKey) {
  return props.field.option_routes?.[optionKey] || '__next__';
}

// Persist a branch target for one option key. '__next__' clears the entry so the
// stored map only holds real branches.
function setRoute(optionKey, value) {
  const next = { ...(props.field.option_routes || {}) };
  if (value === '__next__') {
    delete next[optionKey];
  } else {
    next[optionKey] = value;
  }
  emit('update', { ...props.field, option_routes: next });
}

// --- Conditional logic (visible_when / required_when) ---------------------
const RULE_KEYS = ['visible_when', 'required_when'];

const CMP_OPTIONS = [
  { value: 'eq', labelKey: 'CMP_EQ' },
  { value: 'neq', labelKey: 'CMP_NEQ' },
  { value: 'in', labelKey: 'CMP_IN' },
  { value: 'truthy', labelKey: 'CMP_TRUTHY' },
  { value: 'falsy', labelKey: 'CMP_FALSY' },
];

// Dependency options exclude this field itself.
const dependencyOptions = computed(() =>
  (props.availableFields || []).filter(o => o.value !== props.field.id)
);

function ruleEnabled(key) {
  return !!props.field[key]?.rules?.length;
}

function emitRuleSet(key, ruleSet) {
  const next = { ...props.field };
  if (ruleSet) {
    next[key] = ruleSet;
  } else {
    delete next[key];
  }
  emit('update', next);
}

function toggleRule(key, on) {
  emitRuleSet(
    key,
    on ? { op: 'and', rules: [{ field: '', cmp: 'eq', value: '' }] } : null
  );
}

function setRuleOp(key, op) {
  emitRuleSet(key, { ...(props.field[key] || { rules: [] }), op });
}

function addCondition(key) {
  const cur = props.field[key] || { op: 'and', rules: [] };
  emitRuleSet(key, {
    ...cur,
    rules: [...cur.rules, { field: '', cmp: 'eq', value: '' }],
  });
}

function updateCondition(key, idx, patch) {
  const cur = props.field[key];
  if (!cur) return;
  const rules = cur.rules.map((r, i) => (i === idx ? { ...r, ...patch } : r));
  emitRuleSet(key, { ...cur, rules });
}

function removeCondition(key, idx) {
  const cur = props.field[key];
  if (!cur) return;
  const rules = cur.rules.filter((_, i) => i !== idx);
  emitRuleSet(key, rules.length ? { ...cur, rules } : null);
}

function needsValue(cmp) {
  return cmp !== 'truthy' && cmp !== 'falsy';
}
</script>

<template>
  <div
    class="rounded-lg border bg-n-background p-3 flex flex-col gap-0 group transition-colors"
    :class="[
      hasMappedAttribute
        ? 'border-teal-500/30'
        : 'border-n-weak hover:border-n-strong',
    ]"
    draggable="true"
    @dragstart="handleDragStart"
  >
    <div class="flex items-start gap-3">
      <!-- Drag handle -->
      <div
        class="text-n-slate-8 hover:text-n-slate-10 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
      >
        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <circle cx="9" cy="6" r="1.5" />
          <circle cx="15" cy="6" r="1.5" />
          <circle cx="9" cy="12" r="1.5" />
          <circle cx="15" cy="12" r="1.5" />
          <circle cx="9" cy="18" r="1.5" />
          <circle cx="15" cy="18" r="1.5" />
        </svg>
      </div>

      <!-- Field content -->
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2 mb-1">
          <span
            class="text-xs font-medium px-1.5 py-0.5 rounded shrink-0"
            :class="typeStyle.class"
          >
            {{ typeStyle.label }}
          </span>
          <input
            type="text"
            :value="field.label"
            class="text-sm font-medium text-n-slate-12 bg-transparent border-none px-0 flex-1 min-w-0 focus:outline-none focus:ring-0"
            @input="updateLabel"
          />
          <span
            v-if="field.required"
            class="text-[10px] text-red-400 font-medium shrink-0"
          >
            {{ t('FORM_BUILDER.FIELD.REQUIRED') }}
          </span>
          <span v-else class="text-[10px] text-n-slate-10 font-medium shrink-0">
            {{ t('FORM_BUILDER.FIELD.OPTIONAL') }}
          </span>
        </div>

        <!-- Placeholder -->
        <input
          v-if="!isChoiceType && !isFileType && !isDocumentsType"
          type="text"
          :value="field.placeholder || ''"
          :placeholder="t('FORM_BUILDER.FIELD.PLACEHOLDER_HINT')"
          class="text-xs text-n-slate-10 bg-transparent border-none px-0 w-full focus:outline-none focus:ring-0"
          @input="updatePlaceholder"
        />

        <!-- Tooltip help (shown via a "?" badge next to the label) -->
        <input
          type="text"
          :value="field.tooltip || ''"
          :placeholder="t('FORM_BUILDER.FIELD.TOOLTIP_HINT')"
          class="text-xs text-n-slate-10 bg-transparent border-none px-0 w-full focus:outline-none focus:ring-0"
          @input="updateTooltip"
        />

        <!-- Select options preview -->
        <div
          v-if="isChoiceType && field.options && field.options.length"
          class="flex flex-wrap gap-1 mt-1"
        >
          <span
            v-for="opt in field.options"
            :key="opt"
            class="text-[10px] bg-n-alpha-2 text-n-slate-11 px-1.5 py-0.5 rounded"
          >
            {{ opt }}
          </span>
        </div>

        <!-- File info -->
        <div v-if="isFileType" class="flex items-center gap-3 mt-1">
          <span class="text-[10px] text-n-slate-10">
            {{
              t('FORM_BUILDER.FIELD.MAX_FILES', { count: field.max_files || 5 })
            }}
          </span>
          <span class="text-[10px] text-n-slate-10">
            {{
              t('FORM_BUILDER.FIELD.MAX_SIZE', {
                size: field.max_file_size || 10,
              })
            }}
          </span>
        </div>

        <!-- Mapped attribute badge -->
        <div v-if="hasMappedAttribute" class="flex items-center gap-1.5 mt-1.5">
          <span
            class="inline-flex items-center gap-1 text-[10px] font-medium text-teal-400 bg-teal-400/10 px-1.5 py-0.5 rounded"
          >
            <svg
              class="w-2.5 h-2.5"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              viewBox="0 0 24 24"
            >
              <path
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
              />
            </svg>
            {{ field.mapped_attribute }}
          </span>
          <span class="text-[10px] text-n-slate-9">
            {{ mappedAttributeHint }}
          </span>
        </div>
      </div>

      <!-- Actions -->
      <div
        class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
      >
        <button
          class="w-8 h-8 rounded flex items-center justify-center text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2"
          @click="toggleConfig"
        >
          <svg
            class="w-[18px] h-[18px]"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            viewBox="0 0 24 24"
          >
            <path
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
            />
            <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </button>
        <button
          class="w-8 h-8 rounded flex items-center justify-center text-n-slate-11 hover:text-red-400 hover:bg-n-alpha-2"
          @click="$emit('delete')"
        >
          <svg
            class="w-[18px] h-[18px]"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            viewBox="0 0 24 24"
          >
            <path d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Config Panel -->
    <div v-if="showConfig" class="mt-3 pt-3 border-t border-n-weak">
      <div class="grid grid-cols-2 gap-3">
        <!-- Required toggle -->
        <div class="flex flex-col gap-1">
          <label
            class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
          >
            {{ t('FORM_BUILDER.FIELD.REQUIRED_LABEL') }}
          </label>
          <div class="flex items-center gap-2">
            <button
              class="w-8 h-[18px] rounded-full relative cursor-pointer border-0 p-0 transition-colors"
              :class="field.required ? 'bg-woot-500' : 'bg-n-slate-7'"
              @click="updateRequired(!field.required)"
            >
              <span
                class="absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow-sm transition-transform"
                :class="field.required ? 'left-[17px]' : 'left-[2px]'"
              />
            </button>
            <span class="text-xs text-n-slate-11">
              {{
                field.required
                  ? t('FORM_BUILDER.FIELD.YES')
                  : t('FORM_BUILDER.FIELD.NO')
              }}
            </span>
          </div>
        </div>

        <!-- Visible toggle -->
        <div class="flex flex-col gap-1">
          <label
            class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
          >
            {{ t('FORM_BUILDER.FIELD.VISIBLE_LABEL') }}
          </label>
          <div class="flex items-center gap-2">
            <button
              class="w-8 h-[18px] rounded-full relative cursor-pointer border-0 p-0 transition-colors"
              :class="isVisible ? 'bg-woot-500' : 'bg-n-slate-7'"
              @click="updateVisible(!isVisible)"
            >
              <span
                class="absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow-sm transition-transform"
                :class="isVisible ? 'left-[17px]' : 'left-[2px]'"
              />
            </button>
            <span class="text-xs text-n-slate-11">
              {{
                isVisible
                  ? t('FORM_BUILDER.FIELD.YES')
                  : t('FORM_BUILDER.FIELD.NO')
              }}
            </span>
          </div>
          <span class="text-[10px] text-n-slate-9">
            {{ t('FORM_BUILDER.FIELD.VISIBLE_HINT') }}
          </span>
        </div>

        <!-- Documents: required-document checklist collected inline on the form -->
        <div
          v-if="isDocumentsType"
          class="flex flex-col gap-2 col-span-2 border-t border-n-weak pt-3"
        >
          <div class="flex items-start gap-2">
            <input
              :id="`include-subcategory-templates-${field.id}`"
              type="checkbox"
              :checked="includeSubcategoryTemplates"
              class="mt-0.5"
              @change="toggleIncludeSubcategoryTemplates($event.target.checked)"
            />
            <label
              :for="`include-subcategory-templates-${field.id}`"
              class="flex flex-col gap-0.5 cursor-pointer"
            >
              <span class="text-xs font-medium text-n-slate-11">
                {{ t('FORM_BUILDER.FIELD.DOCUMENTS.INCLUDE_TEMPLATES') }}
              </span>
              <span class="text-[10px] text-n-slate-9">
                {{ t('FORM_BUILDER.FIELD.DOCUMENTS.INCLUDE_TEMPLATES_HINT') }}
              </span>
            </label>
          </div>

          <label
            class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
          >
            {{ t('FORM_BUILDER.FIELD.DOCUMENTS.ADDITIONAL') }}
          </label>
          <DocumentRequestTemplatesEditor
            :model-value="field.documents || []"
            @update:model-value="updateDocuments"
          />
        </div>

        <!-- Default country (phone fields) -->
        <div v-if="isPhoneType" class="flex flex-col gap-1 col-span-2">
          <label
            class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
          >
            {{ t('FORM_BUILDER.FIELD.DEFAULT_COUNTRY') }}
          </label>
          <CountryCodeSelect
            :model-value="field.default_country || 'MX'"
            @update:model-value="updateDefaultCountry"
          />
          <span class="text-[10px] text-n-slate-9">
            {{ t('FORM_BUILDER.FIELD.DEFAULT_COUNTRY_HINT') }}
          </span>
        </div>

        <!-- Validation regex -->
        <div
          v-if="!isChoiceType && !isFileType && !isDocumentsType"
          class="flex flex-col gap-1"
        >
          <label
            class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
          >
            {{ t('FORM_BUILDER.FIELD.VALIDATION') }}
          </label>
          <input
            type="text"
            :value="field.validation_regex || ''"
            :placeholder="t('FORM_BUILDER.FIELD.VALIDATION_PLACEHOLDER')"
            class="w-full px-2.5 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12"
            @input="updateValidation"
          />
        </div>

        <!-- Options for choice types -->
        <div v-if="isChoiceType" class="flex flex-col gap-1 col-span-2">
          <label
            class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
          >
            {{ t('FORM_BUILDER.FIELD.OPTIONS_LABEL') }}
          </label>
          <textarea
            rows="4"
            :value="optionsText"
            :placeholder="t('FORM_BUILDER.FIELD.OPTIONS_PLACEHOLDER')"
            class="w-full px-2.5 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12 placeholder-n-slate-9"
            @input="updateOptions"
          />
          <span class="text-[10px] text-n-slate-9">
            {{ t('FORM_BUILDER.FIELD.OPTIONS_HINT') }}
          </span>
        </div>

        <!-- Display variant (select only): dropdown vs button group -->
        <div v-if="isSelectType" class="flex flex-col gap-1 col-span-2">
          <label
            class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
          >
            {{ t('FORM_BUILDER.FIELD.DISPLAY_LABEL') }}
          </label>
          <select
            :value="field.variant || 'dropdown'"
            class="w-full px-2.5 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12"
            @change="updateVariant"
          >
            <option value="dropdown">
              {{ t('FORM_BUILDER.FIELD.DISPLAY_DROPDOWN') }}
            </option>
            <option value="buttons">
              {{ t('FORM_BUILDER.FIELD.DISPLAY_BUTTONS') }}
            </option>
          </select>
        </div>

        <!-- Button layout (only when displayed as buttons) -->
        <div
          v-if="isSelectType && field.variant === 'buttons'"
          class="flex flex-col gap-1 col-span-2"
        >
          <label
            class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
          >
            {{ t('FORM_BUILDER.FIELD.BUTTON_LAYOUT_LABEL') }}
          </label>
          <select
            :value="field.button_layout || 'horizontal'"
            class="w-full px-2.5 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12"
            @change="updateButtonLayout"
          >
            <option value="horizontal">
              {{ t('FORM_BUILDER.FIELD.BUTTON_LAYOUT_HORIZONTAL') }}
            </option>
            <option value="vertical">
              {{ t('FORM_BUILDER.FIELD.BUTTON_LAYOUT_VERTICAL') }}
            </option>
          </select>
        </div>

        <!-- Branching / logic (select + checkbox) -->
        <div v-if="isChoiceType" class="flex flex-col gap-1.5 col-span-2">
          <label
            class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
          >
            {{ t('FORM_BUILDER.FIELD.LOGIC.TITLE') }}
          </label>

          <!-- Select: one route per option -->
          <template v-if="isSelectType">
            <div
              v-for="opt in field.options || []"
              :key="opt"
              class="flex items-center gap-2"
            >
              <span class="text-xs text-n-slate-11 truncate flex-1 min-w-0">
                {{ opt }}
              </span>
              <svg
                class="w-3.5 h-3.5 text-n-slate-8 shrink-0"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <path d="M9 5l7 7-7 7" />
              </svg>
              <select
                :value="routeFor(opt)"
                class="px-2 py-1.5 rounded-lg bg-n-alpha-1 border border-n-weak text-xs text-n-slate-12 shrink-0 max-w-[55%]"
                @change="e => setRoute(opt, e.target.value)"
              >
                <option
                  v-for="target in targetOptions"
                  :key="target.value"
                  :value="target.value"
                >
                  {{ target.label }}
                </option>
              </select>
            </div>
          </template>

          <!-- Checkbox: one route per boolean state -->
          <template v-else>
            <div
              v-for="state in [
                { key: 'true', label: t('FORM_BUILDER.FIELD.LOGIC.WHEN_CHECKED') },
                {
                  key: 'false',
                  label: t('FORM_BUILDER.FIELD.LOGIC.WHEN_UNCHECKED'),
                },
              ]"
              :key="state.key"
              class="flex items-center gap-2"
            >
              <span class="text-xs text-n-slate-11 truncate flex-1 min-w-0">
                {{ state.label }}
              </span>
              <svg
                class="w-3.5 h-3.5 text-n-slate-8 shrink-0"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <path d="M9 5l7 7-7 7" />
              </svg>
              <select
                :value="routeFor(state.key)"
                class="px-2 py-1.5 rounded-lg bg-n-alpha-1 border border-n-weak text-xs text-n-slate-12 shrink-0 max-w-[55%]"
                @change="e => setRoute(state.key, e.target.value)"
              >
                <option
                  v-for="target in targetOptions"
                  :key="target.value"
                  :value="target.value"
                >
                  {{ target.label }}
                </option>
              </select>
            </div>
          </template>

          <span class="text-[10px] text-n-slate-9">
            {{ t('FORM_BUILDER.FIELD.LOGIC.HINT') }}
          </span>
        </div>

        <!-- File settings -->
        <template v-if="isFileType">
          <div class="flex flex-col gap-1">
            <label
              class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
            >
              {{ t('FORM_BUILDER.FIELD.MAX_FILES_LABEL') }}
            </label>
            <input
              type="number"
              :value="field.max_files || 5"
              min="1"
              max="20"
              class="w-full px-2.5 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12"
              @input="updateMaxFiles"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label
              class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
            >
              {{ t('FORM_BUILDER.FIELD.MAX_SIZE_LABEL') }}
            </label>
            <input
              type="number"
              :value="field.max_file_size || 10"
              min="1"
              max="50"
              class="w-full px-2.5 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12"
              @input="updateMaxFileSize"
            />
          </div>
        </template>

        <!-- Mapped attribute -->
        <div v-if="!isDocumentsType" class="flex flex-col gap-1 col-span-2">
          <label
            class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
          >
            {{ t('FORM_BUILDER.FIELD.MAPPED_ATTRIBUTE') }}
          </label>
          <input
            :id="`mapped-attr-${field.id}`"
            type="text"
            list="mapped-attribute-suggestions"
            :value="field.mapped_attribute || ''"
            :placeholder="t('FORM_BUILDER.FIELD.MAPPED_ATTRIBUTE_PLACEHOLDER')"
            class="w-full px-2.5 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12"
            @input="updateMappedAttribute"
          />
          <datalist id="mapped-attribute-suggestions">
            <option value="ticket.title" />
            <option value="ticket.priority" />
            <option value="ticket.client_name" />
            <option v-if="isContractsEnabled" value="ticket.contract_number" />
            <option value="ticket.address_street" />
            <option value="ticket.address_colony" />
            <option value="ticket.address_postal_code" />
            <option value="ticket.address_city" />
            <option value="ticket.address_state" />
            <option value="ticket.custom.nombre" />
            <option value="ticket.custom.folio_externo" />
            <option value="ticket.custom.contrato" />
            <option value="contact.name" />
            <option value="contact.last_name" />
            <option value="contact.email" />
            <option value="contact.phone_number" />
            <option value="contact.identifier" />
            <option value="contact.custom.tipo_cliente" />
          </datalist>
          <span class="text-[10px] text-n-slate-9">
            {{ t('FORM_BUILDER.FIELD.MAPPED_ATTRIBUTE_HINT') }}
          </span>
        </div>

        <!-- Overwrite existing value (contact-mapped fields only) -->
        <div
          v-if="isContactMappedField"
          class="flex items-start gap-2 col-span-2"
        >
          <input
            :id="`overwrite-existing-${field.id}`"
            type="checkbox"
            :checked="!!field.overwrite_existing"
            class="mt-0.5"
            @change="toggleOverwriteExisting"
          />
          <label
            :for="`overwrite-existing-${field.id}`"
            class="flex flex-col gap-0.5 cursor-pointer"
          >
            <span class="text-xs font-medium text-n-slate-11">
              {{ t('FORM_BUILDER.FIELD.OVERWRITE_EXISTING') }}
            </span>
            <span class="text-[10px] text-n-slate-9">
              {{ t('FORM_BUILDER.FIELD.OVERWRITE_EXISTING_HINT') }}
            </span>
          </label>
        </div>

        <!-- Prefill key -->
        <div v-if="!isDocumentsType" class="flex flex-col gap-1 col-span-2">
          <label
            class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
          >
            {{ t('FORM_BUILDER.FIELD.PREFILL_KEY') }}
          </label>
          <input
            type="text"
            :value="field.prefill_key || ''"
            :placeholder="t('FORM_BUILDER.FIELD.PREFILL_KEY_PLACEHOLDER')"
            class="w-full px-2.5 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12"
            @input="
              e => emit('update', { ...field, prefill_key: e.target.value })
            "
          />
          <span class="text-[10px] text-n-slate-9">
            {{ t('FORM_BUILDER.FIELD.PREFILL_KEY_HINT') }}
          </span>
        </div>

        <!-- Default / predefined value (not for file/documents fields) -->
        <div
          v-if="!isFileType && !isDocumentsType"
          class="flex flex-col gap-1 col-span-2"
        >
          <label
            class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
          >
            {{ t('FORM_BUILDER.FIELD.DEFAULT_VALUE') }}
          </label>

          <!-- Select: pick from the field's own options -->
          <select
            v-if="isSelectType"
            :value="field.default || ''"
            class="w-full px-2.5 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12"
            @change="updateDefault"
          >
            <option value="">{{ t('FORM_BUILDER.FIELD.DEFAULT_VALUE_NONE') }}</option>
            <option v-for="opt in field.options || []" :key="opt" :value="opt">
              {{ opt }}
            </option>
          </select>

          <!-- Checkbox: yes/no toggle storing a boolean -->
          <div v-else-if="isCheckboxType" class="flex items-center gap-2">
            <button
              class="w-8 h-[18px] rounded-full relative cursor-pointer border-0 p-0 transition-colors"
              :class="field.default ? 'bg-woot-500' : 'bg-n-slate-7'"
              @click="updateDefaultBool(!field.default)"
            >
              <span
                class="absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow-sm transition-transform"
                :class="field.default ? 'left-[17px]' : 'left-[2px]'"
              />
            </button>
            <span class="text-xs text-n-slate-11">
              {{
                field.default
                  ? t('FORM_BUILDER.FIELD.YES')
                  : t('FORM_BUILDER.FIELD.NO')
              }}
            </span>
          </div>

          <!-- Text / number / date / email / phone / textarea -->
          <input
            v-else
            :type="defaultInputType"
            :value="field.default || ''"
            :placeholder="t('FORM_BUILDER.FIELD.DEFAULT_VALUE_PLACEHOLDER')"
            class="w-full px-2.5 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12"
            @input="updateDefault"
          />

          <span class="text-[10px] text-n-slate-9">
            {{ t('FORM_BUILDER.FIELD.DEFAULT_VALUE_HINT') }}
          </span>
        </div>

        <!-- Address options: autocomplete + map toggles -->
        <div
          v-if="isAddressType"
          class="grid grid-cols-2 gap-3 col-span-2 border-t border-n-weak pt-3"
        >
          <div class="flex flex-col gap-1">
            <label
              class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
            >
              {{ t('FORM_BUILDER.FIELD.ADDRESS.AUTOCOMPLETE') }}
            </label>
            <button
              class="w-8 h-[18px] rounded-full relative cursor-pointer border-0 p-0 transition-colors"
              :class="addressAutocompleteOn ? 'bg-woot-500' : 'bg-n-slate-7'"
              @click="toggleAutocomplete(!addressAutocompleteOn)"
            >
              <span
                class="absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow-sm transition-transform"
                :class="addressAutocompleteOn ? 'left-[17px]' : 'left-[2px]'"
              />
            </button>
          </div>
          <div class="flex flex-col gap-1">
            <label
              class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
            >
              {{ t('FORM_BUILDER.FIELD.ADDRESS.MAP') }}
            </label>
            <button
              class="w-8 h-[18px] rounded-full relative cursor-pointer border-0 p-0 transition-colors"
              :class="addressMapOn ? 'bg-woot-500' : 'bg-n-slate-7'"
              @click="toggleAddressMap(!addressMapOn)"
            >
              <span
                class="absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow-sm transition-transform"
                :class="addressMapOn ? 'left-[17px]' : 'left-[2px]'"
              />
            </button>
          </div>
          <div class="flex flex-col gap-1 col-span-2">
            <label
              class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
            >
              {{ t('FORM_BUILDER.FIELD.ADDRESS.LOCK') }}
            </label>
            <div class="flex items-center gap-2">
              <button
                class="w-8 h-[18px] rounded-full relative cursor-pointer border-0 p-0 transition-colors"
                :class="addressLockOn ? 'bg-woot-500' : 'bg-n-slate-7'"
                @click="toggleAddressLock(!addressLockOn)"
              >
                <span
                  class="absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow-sm transition-transform"
                  :class="addressLockOn ? 'left-[17px]' : 'left-[2px]'"
                />
              </button>
              <span class="text-[10px] text-n-slate-9">
                {{ t('FORM_BUILDER.FIELD.ADDRESS.LOCK_HINT') }}
              </span>
            </div>
          </div>
          <div class="flex flex-col gap-1 col-span-2">
            <label
              class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
            >
              {{ t('FORM_BUILDER.FIELD.ADDRESS.COUNTRY') }}
            </label>
            <CountryCodeSelect
              :model-value="(field.country || 'MX').toUpperCase()"
              @update:model-value="updateAddressCountry"
            />
            <span class="text-[10px] text-n-slate-9">
              {{ t('FORM_BUILDER.FIELD.ADDRESS.COUNTRY_HINT') }}
            </span>
          </div>
        </div>

        <!-- Address autofill mapping (address fields only) -->
        <div
          v-if="isAddressType"
          class="flex flex-col gap-1.5 col-span-2 border-t border-n-weak pt-3"
        >
          <div class="flex items-center justify-between">
            <label
              class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
            >
              {{ t('FORM_BUILDER.FIELD.AUTOFILL.TITLE') }}
            </label>
            <label
              v-if="addressLockOn"
              class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
            >
              {{ t('FORM_BUILDER.FIELD.AUTOFILL.BLOCKED') }}
            </label>
          </div>
          <div
            v-for="component in ADDRESS_COMPONENTS"
            :key="component"
            class="flex items-center gap-2"
          >
            <span class="text-xs text-n-slate-11 flex-1 min-w-0">
              {{ t(`FORM_BUILDER.FIELD.AUTOFILL.${component.toUpperCase()}`) }}
            </span>
            <svg
              class="w-3.5 h-3.5 text-n-slate-8 shrink-0"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              viewBox="0 0 24 24"
            >
              <path d="M9 5l7 7-7 7" />
            </svg>
            <select
              :value="autofillTarget(component)"
              class="px-2 py-1.5 rounded-lg bg-n-alpha-1 border border-n-weak text-xs text-n-slate-12 shrink-0 max-w-[45%]"
              @change="e => setAutofill(component, e.target.value)"
            >
              <option value="">
                {{ t('FORM_BUILDER.FIELD.AUTOFILL.NONE') }}
              </option>
              <option
                v-for="opt in dependencyOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
            <button
              v-if="addressLockOn && autofillTarget(component)"
              type="button"
              :aria-label="`${component}-blocked`"
              class="w-8 h-[18px] rounded-full relative cursor-pointer border-0 p-0 transition-colors shrink-0"
              :class="isComponentBlocked(component) ? 'bg-woot-500' : 'bg-n-slate-7'"
              @click="toggleComponentEditable(component, isComponentBlocked(component))"
            >
              <span
                class="absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow-sm transition-transform"
                :class="isComponentBlocked(component) ? 'left-[17px]' : 'left-[2px]'"
              />
            </button>
            <span v-else-if="addressLockOn" class="w-8 shrink-0" />
          </div>
          <span class="text-[10px] text-n-slate-9">
            {{ t('FORM_BUILDER.FIELD.AUTOFILL.HINT') }}
          </span>
        </div>

        <!-- Conditional logic: visible_when / required_when -->
        <div
          v-for="key in RULE_KEYS"
          :key="key"
          class="flex flex-col gap-1.5 col-span-2 border-t border-n-weak pt-3"
        >
          <div class="flex items-center justify-between">
            <label
              class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
            >
              {{
                key === 'visible_when'
                  ? t('FORM_BUILDER.FIELD.CONDITION.SHOW_WHEN')
                  : t('FORM_BUILDER.FIELD.CONDITION.REQUIRE_WHEN')
              }}
            </label>
            <button
              class="w-8 h-[18px] rounded-full relative cursor-pointer border-0 p-0 transition-colors"
              :class="ruleEnabled(key) ? 'bg-woot-500' : 'bg-n-slate-7'"
              @click="toggleRule(key, !ruleEnabled(key))"
            >
              <span
                class="absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow-sm transition-transform"
                :class="ruleEnabled(key) ? 'left-[17px]' : 'left-[2px]'"
              />
            </button>
          </div>

          <template v-if="ruleEnabled(key)">
            <select
              :value="field[key].op || 'and'"
              class="px-2 py-1.5 rounded-lg bg-n-alpha-1 border border-n-weak text-xs text-n-slate-12"
              @change="e => setRuleOp(key, e.target.value)"
            >
              <option value="and">
                {{ t('FORM_BUILDER.FIELD.CONDITION.MATCH_ALL') }}
              </option>
              <option value="or">
                {{ t('FORM_BUILDER.FIELD.CONDITION.MATCH_ANY') }}
              </option>
            </select>

            <div
              v-for="(rule, idx) in field[key].rules"
              :key="idx"
              class="flex items-center gap-1.5"
            >
              <select
                :value="rule.field"
                class="px-2 py-1.5 rounded-lg bg-n-alpha-1 border border-n-weak text-xs text-n-slate-12 flex-1 min-w-0"
                @change="e => updateCondition(key, idx, { field: e.target.value })"
              >
                <option value="" disabled>
                  {{ t('FORM_BUILDER.FIELD.CONDITION.FIELD_PLACEHOLDER') }}
                </option>
                <option
                  v-for="opt in dependencyOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </option>
              </select>
              <select
                :value="rule.cmp"
                class="px-2 py-1.5 rounded-lg bg-n-alpha-1 border border-n-weak text-xs text-n-slate-12 shrink-0"
                @change="e => updateCondition(key, idx, { cmp: e.target.value })"
              >
                <option v-for="c in CMP_OPTIONS" :key="c.value" :value="c.value">
                  {{ t(`FORM_BUILDER.FIELD.CONDITION.${c.labelKey}`) }}
                </option>
              </select>
              <input
                v-if="needsValue(rule.cmp)"
                :value="rule.value"
                :placeholder="t('FORM_BUILDER.FIELD.CONDITION.VALUE_PLACEHOLDER')"
                class="px-2 py-1.5 rounded-lg bg-n-alpha-1 border border-n-weak text-xs text-n-slate-12 w-[28%] shrink-0"
                @input="e => updateCondition(key, idx, { value: e.target.value })"
              />
              <button
                class="text-n-slate-8 hover:text-red-400 shrink-0"
                @click="removeCondition(key, idx)"
              >
                <svg
                  class="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <button
              class="text-[11px] text-woot-500 hover:underline self-start"
              @click="addCondition(key)"
            >
              + {{ t('FORM_BUILDER.FIELD.CONDITION.ADD') }}
            </button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
